<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import QRBuhoCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/QRBuhoCampaign/QRBuhoCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';

const { t } = useI18n();
const store = useStore();
const [showQRBuhoCampaignDialog, toggleQRBuhoCampaignDialog] = useToggle();

const selectedCampaign = ref(null);
const dialogMode = ref('create');
const confirmDeleteCampaignDialogRef = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const QRBuhoCampaigns = computed(
  () => store.getters['campaigns/getQRBuhoCampaigns']
);

const hasNoQRBuhoCampaigns = computed(
  () => QRBuhoCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleCreateCampaign = () => {
  selectedCampaign.value = null;
  dialogMode.value = 'create';
  toggleQRBuhoCampaignDialog();
};

const handleEdit = campaign => {
  selectedCampaign.value = campaign;
  dialogMode.value = 'edit';
  toggleQRBuhoCampaignDialog(true);
};

const handleClone = campaign => {
  selectedCampaign.value = campaign;
  dialogMode.value = 'clone';
  toggleQRBuhoCampaignDialog(true);
};

const handleCloseDialog = () => {
  toggleQRBuhoCampaignDialog(false);
  selectedCampaign.value = null;
  dialogMode.value = 'create';
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

onMounted(() => {
  store.dispatch('campaigns/get');
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.QRBUHO.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.QRBUHO.NEW_CAMPAIGN')"
    @click="handleCreateCampaign"
    @close="handleCloseDialog"
  >
    <template #action>
      <QRBuhoCampaignDialog
        v-if="showQRBuhoCampaignDialog"
        :campaign="selectedCampaign"
        :mode="dialogMode"
        @close="handleCloseDialog"
      />
    </template>

    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <CampaignList
      v-else-if="!hasNoQRBuhoCampaigns"
      :campaigns="QRBuhoCampaigns"
      @edit="handleEdit"
      @clone="handleClone"
      @delete="handleDelete"
    />

    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.QRBUHO.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.QRBUHO.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />

    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
